import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Field } from "@/pages/Companies";
import { toast } from "sonner";
import { Mail, Loader2, Send, AlertTriangle, Eye, EyeOff, ShieldAlert } from "lucide-react";

export default function EmailQuoteDialog({ quote, open, onClose, onSent }) {
  const [toEmail, setToEmail] = useState("");
  const [templateKey, setTemplateKey] = useState("quote_send");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const { data: templates = [] } = useQuery({ queryKey: ["templates-for-email"], queryFn: async () => (await api.get("/email/templates")).data, enabled: open });
  const { data: status } = useQuery({ queryKey: ["email-status"], queryFn: async () => (await api.get("/email/status")).data, enabled: open });

  useEffect(() => {
    if (!open || !quote) return;
    setToEmail(quote.customer_email || "");
    const tpl = templates.find((t) => t.key === templateKey) || templates[0];
    if (tpl) { setSubject(tpl.subject); setBody(tpl.body_html); }
    setShowPreview(false); setPreview(null); setConfirming(false);
  }, [open, quote, templateKey, templates]);

  const doPreview = async () => {
    if (!toEmail) { toast.error("Enter a recipient email"); return; }
    setPreviewing(true);
    try {
      const { data } = await api.post("/email/preview-quote", {
        quote_id: quote.id, to_email: toEmail, template_key: templateKey,
        subject, body_html: body,
      });
      setPreview(data);
      setShowPreview(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  };

  const send = async () => {
    if (!toEmail) { toast.error("Enter a recipient email"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/email/send-quote", {
        quote_id: quote.id, to_email: toEmail, template_key: templateKey,
        subject, body_html: body,
      });
      if (data.status === "sent") toast.success(`Email sent to ${toEmail}`);
      else if (data.status === "mocked") toast.warning(`MOCK send — email logged but not delivered (no Resend key)`);
      else toast.error(data.error || "Send failed");
      onSent?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); setConfirming(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-2xl" data-testid="email-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Mail size={18} className="text-cyan-600" /> Email quote {quote?.quote_number}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Choose a template, preview, then confirm to send.
          </DialogDescription>
        </DialogHeader>

        {status && !status.live && (
          <div className="border border-amber-200 bg-amber-50 px-3 py-2 rounded-sm text-xs text-amber-900 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
            <span><b>MOCK mode:</b> email will be logged but not delivered. Add a <b>RESEND_API_KEY</b> to enable live sending.</span>
          </div>
        )}

        <div className="space-y-3 mt-1">
          <div className="grid grid-cols-2 gap-3">
            <Field label="To" required><Input data-testid="email-to-input" type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="customer@example.com" /></Field>
            <Field label="Template">
              <Select value={templateKey} onValueChange={(v) => setTemplateKey(v)}>
                <SelectTrigger data-testid="email-template-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.id} value={t.key}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Subject"><Input data-testid="email-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
          <Field label="Body (HTML — variables auto-filled from quote)">
            <Textarea data-testid="email-body-input" rows={10} className="font-mono text-xs" value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>
          <div className="flex flex-wrap gap-1 text-[10px]">
            {["customer_name", "quote_number", "fence_type", "site_address", "scope_summary", "sale_price"].map((v) => (
              <Badge key={v} variant="outline" className="rounded-sm font-mono text-[9px] border-slate-300 cursor-pointer hover:bg-cyan-50" onClick={() => setBody(body + ` {{${v}}}`)}>
                {`{{${v}}}`}
              </Badge>
            ))}
          </div>
        </div>

        {/* Verification banner — always visible */}
        <div className="border-l-4 border-amber-400 bg-amber-50 px-3 py-2.5 rounded-sm flex items-start gap-2" data-testid="template-send-banner">
          <ShieldAlert size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 leading-snug">
            <b>Email is ready to send.</b> Preview the message and confirm the recipient — nothing is delivered until you press <b>Confirm &amp; Send</b>.
          </div>
        </div>

        {showPreview && preview?.html_body && (
          <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="template-email-preview">
            <div className="bg-slate-100 border-b border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
              <span>Preview — what the client will see</span>
              <button onClick={() => setShowPreview(false)} className="text-slate-400 hover:text-slate-700 normal-case text-[10px] inline-flex items-center gap-1" data-testid="template-hide-preview-btn">
                <EyeOff size={11} /> Hide
              </button>
            </div>
            <iframe title="Email preview" srcDoc={preview.html_body} sandbox="" className="w-full h-[380px] bg-white" data-testid="template-preview-iframe" />
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
          <Button variant="outline" onClick={onClose} disabled={sending} className="rounded-sm">Cancel</Button>
          <Button variant="outline" onClick={doPreview} disabled={previewing || sending || !toEmail}
            className="rounded-sm" data-testid="template-preview-btn">
            {previewing ? <Loader2 size={14} className="animate-spin mr-1.5" /> :
              (showPreview ? <EyeOff size={14} className="mr-1.5" /> : <Eye size={14} className="mr-1.5" />)}
            {showPreview ? "Refresh preview" : "Preview message"}
          </Button>
          {confirming ? (
            <Button data-testid="email-send-btn" onClick={send} disabled={sending || !toEmail} className="rounded-sm bg-emerald-600 hover:bg-emerald-700">
              {sending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
              Confirm &amp; Send to {toEmail}
            </Button>
          ) : (
            <Button data-testid="email-request-send-btn" onClick={() => setConfirming(true)} disabled={sending || !toEmail} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">
              <Send size={14} className="mr-1.5" /> Send email…
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
